import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    ConfigurationService, SubjectType, QuoteStoreService, SymbolSearchService,
    EventsService, PortfolioService, InfraUpdate
} from '@app/_shared';
import { OrdersResponse, TradesResponse, PositionsResponse } from '../books.interfaces';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BooksSocketService } from '@app/_shared/providers/websocket/books-socket/books-socket.service';
import { BookType } from '../books.enums';
import { OrderBook, TradeBook, PositionBook } from '../books.models';
import { Utils, OPEN_ORDERS, CustomEvents } from '@app/_helpers';
import { PRODUCT_TYPE_NUM, ALERT_TYPE } from '@app/_shared/shared.enums';
import { AuthService } from '@app/modules/auth';
// import { AlgowisePositionDataService } from '@app/_shared/providers/algowise-position-data/algowise-position-data.service';


export interface StatsSum {
    'mtm': number,
    'booked_pnl': number,
    'net_pnl': number,
    'target_profit': number,
    'stop_loss': number,
    'target_profit_type'?: number
    'stop_loss_type'?: number
}
@Injectable({
    providedIn: 'root'
})
export class BooksService implements OnDestroy {
    firstFetchDone = {
        [BookType.ORDERBOOK]: false,
        [BookType.TRADEBOOK]: false,
        [BookType.POSITIONSBOOK]: false,
    }

    fetchBooksCallStatusSuccess = {
        [BookType.ORDERBOOK]: true,
        [BookType.TRADEBOOK]: true,
        [BookType.POSITIONSBOOK]: true,
    }

    cachedBookData = new Map<string, any[]>();

    // Used to store the last sequence number till where the records are already present in memory
    cachedBookLastSequenceNumber = new Map<string, any>([
        [BookType.ORDERBOOK, { live_seq_num: -1, forward_seq_num: -1 }],
        [BookType.TRADEBOOK, { live_seq_num: -1, forward_seq_num: -1 }],
        [BookType.POSITIONSBOOK, { live_seq_num: -1, forward_seq_num: -1 }],
    ]);

    orderBookOrderIdIndexMap = new Map<string, OrderBook>();
    bookStats = {
        "open_orders": 0,
        "active_algos": 0,
        "filled": 0
    }
    subscribedPositionsSymbols = [];
    orderBookUpdate = new EventEmitter();
    tradeBookUpdate = new EventEmitter<null | TradeBook[]>();
    positionBookUpdate = new EventEmitter();
    algoNameToUpdate = new Set<TradeBook | OrderBook>();

    constructor(
        private http: HttpClient,
        private configurationService: ConfigurationService,
        private booksSocketService: BooksSocketService,
        private quoteStoreService: QuoteStoreService,
        private symbolSearchService: SymbolSearchService,
        private eventsService: EventsService,
        private portfolioService: PortfolioService,
        private authService: AuthService
    ) {
        this.booksSocketService.getSubject(SubjectType.ORDERS).subscribe(orders => {
            if (orders.length) {
                this.refreshBookMap(BookType.ORDERBOOK, orders);
            }
        });

        this.booksSocketService.getSubject(SubjectType.TRADES).subscribe(trades => {
            if (trades.length) {
                this.refreshBookMap(BookType.TRADEBOOK, trades);
            }
        });

        this.booksSocketService.getSubject(SubjectType.POSITIONS).subscribe(positions => {
            if (positions.length) {
                this.refreshBookMap(BookType.POSITIONSBOOK, positions);
            }
        });

        // In case of DMS reconnect, we need to fetch the delta of records, in case any were missed
        // any records from socket but already entered in DB.
        this.booksSocketService.getSubject(SubjectType.STATUS).subscribe(data => {
            if (data['connection']) {
                !this.authService.isAdminLogin && this.firstFetchAll();
            }
        });

        this.eventsService.subscribe(CustomEvents.StreamReconnected, () => {
            !this.authService.isAdminLogin && this.firstFetchAll();
        })

        // If DMS reconnected then fetch books
        this.booksSocketService.getSubject(SubjectType.INFRASTATUS).subscribe((infraUpdate: InfraUpdate) => {
            if (infraUpdate.DMS_ZMQ) {
                !this.authService.isAdminLogin && this.firstFetchAll();
            }
        })

        // Handling a rare race condition. Ideally, when a new strategy is created and then deployed, the
        // running sucess messages comes to FE and FE will cache that strategy status in strategyService. After
        // that that strategies order/trade will come which will read strategy name from that cached data (Which
        // do not comes in books)
        // The race condition is b/w the processing of the strategy status and receving order/trade.
        // If trade is received earlier, it will not be able to find strategyName hence, TB will show NA in strategy name
        // To fix this rare case what we are doing is if we are not able to find strategy name we will add it in algoNameToUpdate
        // set. Whenever any strategy update is received via socket `UpdateRunningCompletedStrategy` event is triggered.
        // And when that event is triggered we will update all the pending names in that set.
        this.eventsService.subscribe(CustomEvents.PortfolioStatusEvent, () => {
            setTimeout(() => {
                this.algoNameToUpdate.forEach((row) => {
                    row.updateAlgoName()
                });
                this.algoNameToUpdate.clear();
            }, 2000); // Will run this saftey check after two seconds
        })
    }

    // TODO: Optimization: Maps
    calcActiveAlgos() {
        if (this.cachedBookData.has(BookType.ORDERBOOK)) {
            const algos = new Set();
            this.cachedBookData.get(BookType.ORDERBOOK).forEach(order => {
                algos.add(order.algo_id);
            });
            return algos.size;
        }
    }
    updateStats() {
        if (this.cachedBookData.has(BookType.ORDERBOOK)) {
            this.bookStats['open_orders'] = this.cachedBookData.get(BookType.ORDERBOOK).filter(od => OPEN_ORDERS.includes(od.order_status)).length;
            this.bookStats['active_algos'] = this.calcActiveAlgos();
        }

        if (this.cachedBookData.has(BookType.TRADEBOOK)) {
            this.bookStats['filled'] = this.cachedBookData.get(BookType.TRADEBOOK).length;
        }
    }

    updateMap(orderBookObjs: OrderBook[]) {
        orderBookObjs.forEach(orderBookObj => {
            this.orderBookOrderIdIndexMap.set(this.getKey(orderBookObj['rapid_order_id']), orderBookObj);
        });
    }


    getKey(...args) {
        return args.join('_');
    }

    // First fetches the delta
    firstFetchAll() {
        this.fetchOrderFirstFetch(this.cachedBookLastSequenceNumber.get(BookType.ORDERBOOK)).subscribe();
        this.fetchTradeFirstFetch(this.cachedBookLastSequenceNumber.get(BookType.TRADEBOOK)).subscribe();
        this.fetchPositionFirstFetch(this.cachedBookLastSequenceNumber.get(BookType.POSITIONSBOOK)).subscribe();
    }

    // Fetches Orders based on sequence number of the last recored present
    fetchOrderFirstFetch(lastSequenceNumber = { live_seq_num: -1, forward_seq_num: -1 }) {
        const url = this.configurationService.applicationUrls.getOrderBook;

        // Only make first fetch call when it is never made before.
        // If request is already made, return cached response.
        if (this.firstFetchDone[BookType.ORDERBOOK] &&
            lastSequenceNumber.live_seq_num === -1 &&
            lastSequenceNumber.forward_seq_num === -1) {
            if (!this.cachedBookData.has(BookType.ORDERBOOK)) {
                this.cachedBookData.set(BookType.ORDERBOOK, []);
            }
            return of(this.cachedBookData.get(BookType.ORDERBOOK));
        } else {
            return this.http.get<OrdersResponse[]>(url, {
                params: { live_seq_num: lastSequenceNumber.live_seq_num, forward_seq_num: lastSequenceNumber.forward_seq_num } as any
            }).pipe(
                map(response => {
                   if (this.firstFetchDone[BookType.ORDERBOOK]) {
                        this.refreshBookMap(BookType.ORDERBOOK, response);
                        return this.cachedBookData.get(BookType.ORDERBOOK);
                    } else {
                        if (response?.length) {
                            const data = [];
                            const seqStats = this.cachedBookLastSequenceNumber.get(BookType.ORDERBOOK);
                            response.forEach(item => {
                                const newOrder = new OrderBook(item);
                                if (!newOrder.strategy_name) this.algoNameToUpdate.add(newOrder);
                                data.unshift(newOrder);
                                // maintaining sequence numbers for live_seq_num and forward_seq_num
                                if (item.strategy_type === ALERT_TYPE.LIVE) {
                                    seqStats['live_seq_num'] = item.sequence_number
                                } else if (item.strategy_type === ALERT_TYPE.FORWARD) {
                                    seqStats['forward_seq_num'] = item.sequence_number
                                }
                            });
                            this.cachedBookData.set(BookType.ORDERBOOK, data);
                            this.cachedBookLastSequenceNumber.set(BookType.ORDERBOOK, seqStats)
                            this.updateMap(data);
                            this.updateStats();
                            this.orderBookUpdate.emit();
                            this.firstFetchDone[BookType.ORDERBOOK] = true;
                            this.fetchBooksCallStatusSuccess[BookType.ORDERBOOK] = true;
                            return this.cachedBookData.get(BookType.ORDERBOOK);
                        } else {
                            this.firstFetchDone[BookType.ORDERBOOK] = true;
                            this.fetchBooksCallStatusSuccess[BookType.ORDERBOOK] = true;
                            this.orderBookUpdate.emit();
                            return [];
                        }
                    }
                }),
                catchError(err => {
                    this.orderBookUpdate.emit();
                    this.fetchBooksCallStatusSuccess[BookType.ORDERBOOK] = false;
                    return [];
                })
            );
        }

    }

    // Fetches Trades based on sequence number of the last recored present
    fetchTradeFirstFetch(lastSequenceNumber = { live_seq_num: -1, forward_seq_num: -1 }) {
        const url = this.configurationService.applicationUrls.getTradeBook;

        // Only make first fetch call when it is never made before.
        // If request is already made, return cached response.
        if (this.firstFetchDone[BookType.TRADEBOOK] &&
            lastSequenceNumber.live_seq_num === -1 &&
            lastSequenceNumber.forward_seq_num === -1) {
            if (!this.cachedBookData.has(BookType.TRADEBOOK)) {
                this.cachedBookData.set(BookType.TRADEBOOK, []);
            }
            return of(this.cachedBookData.get(BookType.TRADEBOOK));
        } else {
            return this.http.get<TradesResponse[]>(url, {
                params: { live_seq_num: lastSequenceNumber.live_seq_num, forward_seq_num: lastSequenceNumber.forward_seq_num } as any
            }).pipe(
                map(response => {
                    if (this.firstFetchDone[BookType.TRADEBOOK]) {
                        this.refreshBookMap(BookType.TRADEBOOK, response);
                        return this.cachedBookData.get(BookType.TRADEBOOK);
                    } else {
                        if (response?.length) {
                            const data = [];
                            const seqStats = this.cachedBookLastSequenceNumber.get(BookType.TRADEBOOK);;
                            response.forEach(item => {
                                const newTrade = new TradeBook(item);
                                if (!newTrade.strategy_name) this.algoNameToUpdate.add(newTrade);
                                data.unshift(newTrade);
                                // maintaining sequence numbers for live_seq_num and forward_seq_num
                                if (item.strategy_type === ALERT_TYPE.LIVE) {
                                    seqStats['live_seq_num'] = item.sequence_number
                                } else if (item.strategy_type === ALERT_TYPE.FORWARD) {
                                    seqStats['forward_seq_num'] = item.sequence_number
                                }
                            });
                            this.cachedBookLastSequenceNumber.set(BookType.TRADEBOOK, seqStats)
                            this.cachedBookData.set(BookType.TRADEBOOK, data);
                            this.updateStats();
                            this.tradeBookUpdate.emit(data);
                            this.firstFetchDone[BookType.TRADEBOOK] = true;
                            this.fetchBooksCallStatusSuccess[BookType.TRADEBOOK] = true;
                            return this.cachedBookData.get(BookType.TRADEBOOK);
                        } else {
                            this.firstFetchDone[BookType.TRADEBOOK] = true;
                            this.fetchBooksCallStatusSuccess[BookType.TRADEBOOK] = true;
                            this.tradeBookUpdate.emit();
                            return [];
                        }
                    }
                }),
                catchError(err => {
                    this.tradeBookUpdate.emit();
                    this.fetchBooksCallStatusSuccess[BookType.TRADEBOOK] = false;
                    return [];
                })
            );
        }

    }

    // Fetches Positions based on sequence number of the last recored present
    fetchPositionFirstFetch(lastSequenceNumber = { live_seq_num: -1, forward_seq_num: -1 }): Observable<PositionBook[]> {
        const url = this.configurationService.applicationUrls.getPositionBook;

        // Only make first fetch call when it is never made before.
        // If request is already made, return cached response.
        if (this.firstFetchDone[BookType.POSITIONSBOOK] &&
            lastSequenceNumber.live_seq_num === -1 &&
            lastSequenceNumber.forward_seq_num === -1) {
            if (!this.cachedBookData.has(BookType.POSITIONSBOOK)) {
                this.cachedBookData.set(BookType.POSITIONSBOOK, []);
            }
            return of(this.cachedBookData.get(BookType.POSITIONSBOOK));
        } else {
            return this.http.get<PositionsResponse[]>(url, {
                params: { live_seq_num: lastSequenceNumber.live_seq_num, forward_seq_num: lastSequenceNumber.forward_seq_num } as any
            }).pipe(
                map(response => {

                    if (this.firstFetchDone[BookType.POSITIONSBOOK]) {
                        this.refreshBookMap(BookType.POSITIONSBOOK, response);
                        return this.cachedBookData.get(BookType.POSITIONSBOOK);
                    } else {
                        if (response?.length) {
                            const data: PositionBook[] = [];
                            const seqStats = this.cachedBookLastSequenceNumber.get(BookType.POSITIONSBOOK);;
                            response.forEach(item => {
                                const position = new PositionBook(item, this.symbolSearchService);
                                // TODO: need to maintain security id and market id in DB.
                                const splittedSymbolId = Utils.extractFromSymbolId(position.symbol_id);
                                const quoteObj = this.quoteStoreService.getFeedObj(splittedSymbolId[1], splittedSymbolId[3]);
                                position.listenLtp(quoteObj);
                                data.push(position);
                                // maintaining sequence numbers for live_seq_num and forward_seq_num
                                if (item.strategy_type === ALERT_TYPE.LIVE) {
                                    seqStats['live_seq_num'] = item.sequence_number
                                } else if (item.strategy_type === ALERT_TYPE.FORWARD) {
                                    seqStats['forward_seq_num'] = item.sequence_number
                                }
                            });
                            this.cachedBookLastSequenceNumber.set(BookType.POSITIONSBOOK, seqStats)
                            this.cachedBookData.set(BookType.POSITIONSBOOK, data);
                            this.updateStats();
                            this.positionBookUpdate.emit();
                            this.firstFetchDone[BookType.POSITIONSBOOK] = true;
                            return this.cachedBookData.get(BookType.POSITIONSBOOK);
                        } else {
                            this.firstFetchDone[BookType.POSITIONSBOOK] = true;
                            return [];
                        }
                    }
                })
            )
        }
    }

    refreshBookMap(bookType: BookType, dataArr) {

        /**
         * In case user didn't opened any book yet (i.e. No fetch is done) ignore this
         * confirmation as it will come in the first fetch itself otherwise handle
         * live confirmation.
         */
        if (!this.firstFetchDone[bookType]) {
            return;
        }

        switch (bookType) {
            case BookType.ORDERBOOK:
                const seqStatsOrders = this.cachedBookLastSequenceNumber.get(BookType.ORDERBOOK);;
                if (!this.cachedBookData.has(BookType.ORDERBOOK)) {
                    this.cachedBookData.set(BookType.ORDERBOOK, []);
                }
                dataArr.forEach((data: OrdersResponse) => {
                    const key = this.getKey(data['rapid_order_id']);
                    if (this.orderBookOrderIdIndexMap.has(key)) {
                        /**
                         * ==== TICKET
                         * RA-401
                         *
                         * === FIX
                         * According to this ticket orderbook should always show latest state of order.
                         * Even if it is traded. Earlier we were removing order when traded.
                         */
                        this.orderBookOrderIdIndexMap.get(key).updateOrderBook(data);
                    } else {
                        const order = new OrderBook(data);
                        if (!order.strategy_name) {
                            this.algoNameToUpdate.add(order);
                        }
                        this.cachedBookData.get(BookType.ORDERBOOK).unshift(order);
                        this.orderBookOrderIdIndexMap.set(key, order);
                    }

                    if (data.strategy_type === ALERT_TYPE.LIVE) {
                        seqStatsOrders['live_seq_num'] = data.sequence_number
                    } else if (data.strategy_type === ALERT_TYPE.FORWARD) {
                        seqStatsOrders['forward_seq_num'] = data.sequence_number
                    }
                });
                this.cachedBookLastSequenceNumber.set(BookType.ORDERBOOK, seqStatsOrders)
                this.updateMap(this.cachedBookData.get(BookType.ORDERBOOK));
                this.updateStats();
                this.orderBookUpdate.emit();

                break;

            case BookType.TRADEBOOK:
                const seqStatsTrades = this.cachedBookLastSequenceNumber.get(BookType.TRADEBOOK);
                console.log("Trade Received", dataArr);
                if (!this.cachedBookData.has(BookType.TRADEBOOK)) {
                    this.cachedBookData.set(BookType.TRADEBOOK, []);
                }
                const newData = [];
                const tradeData = this.cachedBookData.get(BookType.TRADEBOOK);
                dataArr.forEach(data => {
                    const key = this.getKey(data['trade_id'], data['rapid_order_id']);
                    const matchIndex = tradeData.findIndex(el => this.getKey(el['trade_id'], el['rapid_order_id']) === key);
                    console.log("Creating new Trade, matchIndex", matchIndex);

                    const tradeObj = new TradeBook(data);
                    console.log("Trade Object", tradeObj);
                    
                    if (matchIndex > -1) {
                        console.log("Calling updateTradeBook on data", data);
                        this.cachedBookData.get(BookType.TRADEBOOK)[matchIndex].updateTradeBook(data);
                    } else {
                        console.log("Unshifting data", tradeObj);
                        this.cachedBookData.get(BookType.TRADEBOOK).unshift(tradeObj);
                        // RA-891 Only send new data for update (this new data is used to update position data in active algo)
                        // On DMS restart, DMS sends all the rows again so will be sending only delta of trades.
                        newData.unshift(tradeObj);
                    }

                    if (data.strategy_type === ALERT_TYPE.LIVE) {
                        seqStatsTrades['live_seq_num'] = data.sequence_number
                    } else if (data.strategy_type === ALERT_TYPE.FORWARD) {
                        seqStatsTrades['forward_seq_num'] = data.sequence_number
                    }
                });
                this.cachedBookLastSequenceNumber.set(BookType.TRADEBOOK, seqStatsTrades)
                this.updateStats();
                console.log("emmiting event");
                this.tradeBookUpdate.emit(newData);

                break;

            case BookType.POSITIONSBOOK:
                const seqStatsPositions = this.cachedBookLastSequenceNumber.get(BookType.POSITIONSBOOK);;
                if (!this.cachedBookData.has(BookType.POSITIONSBOOK)) {
                    this.cachedBookData.set(BookType.POSITIONSBOOK, []);
                }
                dataArr.forEach(data => {

                    let isPositionUpdate = false;
                    this.cachedBookData.get(BookType.POSITIONSBOOK).forEach((item, index) => {
                        // Unique identification for positions.
                        const oldKey = this.getKey(item['scrip_name'], item['product'], item['broker'], item['position_type']);
                        const liveKey = this.getKey(data['scrip_name'], PRODUCT_TYPE_NUM[data['product']], data['broker'], ALERT_TYPE[data['position_type']]);
                        if (oldKey === liveKey) {
                            isPositionUpdate = true;
                            const positionObj = new PositionBook(data, this.symbolSearchService);
                            const splittedSymbolId = Utils.extractFromSymbolId(positionObj.symbol_id);
                            const quoteObj = this.quoteStoreService.getFeedObj(splittedSymbolId[1], splittedSymbolId[3]);
                            positionObj.listenLtp(quoteObj);
                            this.cachedBookData.get(BookType.POSITIONSBOOK)[index] = positionObj;
                        }
                    });
                    if (!isPositionUpdate) {
                        const positionObj = new PositionBook(data, this.symbolSearchService);
                        const splittedSymbolId = Utils.extractFromSymbolId(positionObj.symbol_id);
                        const quoteObj = this.quoteStoreService.getFeedObj(splittedSymbolId[1], splittedSymbolId[3]);
                        positionObj.listenLtp(quoteObj);
                        if (!this.cachedBookData.has(BookType.POSITIONSBOOK)) {
                            this.cachedBookData.set(BookType.POSITIONSBOOK, []);
                        }
                        this.cachedBookData.get(BookType.POSITIONSBOOK).unshift(positionObj);
                    }
                    if (data.strategy_type === ALERT_TYPE.LIVE) {
                        seqStatsPositions['live_seq_num'] = data.sequence_number
                    } else if (data.strategy_type === ALERT_TYPE.FORWARD) {
                        seqStatsPositions['forward_seq_num'] = data.sequence_number
                    }
                });
                this.cachedBookLastSequenceNumber.set(BookType.POSITIONSBOOK, seqStatsPositions)
                this.updateStats();
                this.positionBookUpdate.emit();
                break;
        }

    }

    ngOnDestroy(): void {
        this.eventsService.unsubscribe(CustomEvents.PortfolioStatusEvent)
    }
}
